/**
 * 路線図 View で、バスを**どの線の脇に置くか**を決める。
 *
 * 線は 3 本ある。
 *
 *     本線     昭和通り〜3号線の回廊。節（停留所）が並んでいる
 *     都市高速  節を飛ぶ区間。**右に 1 本だけ引く**（道は 1 本なので）
 *     支線     回廊の外の並び（香椎浜まわり・下原）
 *     別の道   支線の同じ区間にある 2 本目（城浜団地⇔イオンモールの
 *              香椎浜南公園前回り）
 *
 * ## 判定はバックエンドがしてある
 *
 * **時刻表の飛びを見ないと決められない。**「蔵本の次が香椎」という 17 節の
 * 飛びは、都市高速（10 分）と香椎浜まわりの地上（19 分）の両方で起きる。
 * 節番号だけでは分かれないので、`LineMap::pathOf` が時刻表の生の並びを見て
 * 段に `express` / `spur` を付けてある（規則は `config/linemap.php`）。
 * ここはその印を読むだけ。
 *
 * ## 停まっているバスは本線の脇に置く
 *
 * **次の区間が高速でも、停留所にいるあいだは地上にいる。**蔵本に停まって
 * いるバスを高速の脇へ寄せると、そこで乗降していることが読めない。
 *
 * ## 枝へ渡る区間は、枝の側に乗せる
 *
 * イオンモール香椎浜（香椎浜の枝）から香椎浜北公園前（照葉の枝）へ渡る
 * 区間を香椎浜の側に置くと、**香椎浜車庫の真上を縦に降りてしまう。**
 * 照葉は香椎浜から分かれているので、線は分岐から照葉の幹へ移る。
 * **分かれたほう（子）の車線を採る。**
 *
 * ## 繋がっていない枝どうしの渡りは、帯の脇
 *
 * 快速 1 番は香椎浜北公園前（照葉の枝）から千早駅（千早駅の枝）へ、
 * 3 号線を横切って直行する（25 便）。**どちらの枝にも道は無い。**
 * 片方の線に乗せると、そこに並ぶ停留所を通ってしまう（照葉の枝に
 * 乗せると香椎浜海岸通り・香椎浜三丁目・香椎浜中央の真上を通った）。
 *
 * 帯の脇に置けば、通るのは回廊の節だけ — **それは実際に通っている。**
 *
 * ## 停留所を持たない道は、段ではなく区間に印が付く
 *
 * 1 番は千早六丁目から御幸町へ抜け、香椎浜車庫から香椎浜北公園前へ渡る。
 * どちらも道だけで停留所が無いので、**段の `alt` には出てこない。**
 * 段だけを見ると本道に乗せてしまい、イオンモール香椎浜や千早六丁目を
 * 通過しているように見える。`LineMap::viaOf` が区間に付けた `via` を読む。
 */

/**
 * バスを置く線と、その左右。
 *
 *     lane     rail 本線 / bypass 都市高速 / spur 支線 / alt 支線の別の道
 *     side     left か right。**支線は地形に合わせて左右が変わる**
 *              （香椎浜は海側で右、下原は内陸側で左）
 *     altLane  別の道が本道の内か外か（inner / outer）。ほかは null
 *     spurLane 支線の車線（本線からの遠さ。1 始まり）。ほかは 1
 *
 * @returns {{lane: string, side: string, altLane: string|null,
 *            spurLane: number}}
 */
/**
 * 向きを決めるのに要る、まとまった移動（節）。**半停留所ぶん＝40px。**
 *
 * これより小さい行き戻りは向きの変わり目と見ない。**バックエンドの
 * `LineMap::DIRECTION_MIN` と揃えること。**
 */
const DIRECTION_MIN = 0.5;

export function laneOf(path, index, moving, spurs) {
  const rail = { lane: "rail", side: "right", altLane: null, spurLane: 1 };
  if (! Array.isArray(path)) return rail;

  const here = path[index];
  if (! here) return rail;

  const next = moving ? path[index + 1] : null;

  if (moving && here.express) {
    return { lane: "bypass", side: "right", altLane: null, spurLane: 1 };
  }
  // **この区間だけ別の道に乗る。**停留所を持たない道（1 番の
  // 千早六丁目⇔御幸町・香椎浜車庫⇔香椎浜北公園前）はこれしか手がかりが無い
  if (moving && here.via) {
    const road = (spurs || []).find((s) => s.key === here.via.spur);

    return {
      lane: "alt",
      side: road?.side ?? "right",
      altLane: here.via.alt,
      spurLane: road?.lane ?? 1,
    };
  }
  // **支線へ入る区間・出る区間も支線の脇。**香椎（節）から千早六丁目へ
  // 向かうあいだは、もう本線から外れている。
  // 両端が別の枝なら**分かれたほう**に乗せる — 親に置くとその幹に並ぶ
  // 停留所の真上を縦に通る（香椎浜車庫）。**親子でなければ帯の脇**
  const on = outerOf(here, next, spurs);
  if (! on) return rail;

  const spur = (spurs || []).find((s) => s.key === on.spur);
  const side = spur?.side ?? "right";
  const spurLane = spur?.lane ?? 1;

  // **別の道は本道とは別の線の上。**本道の停留所は通らない。
  // 印は**乗せる側の段から**読む — 線が違うと内と外がずれる
  const other = on === here ? next : here;
  const altLane = (on.alt ?? (moving ? other?.alt : null)) ?? null;
  if (altLane) return { lane: "alt", side, altLane, spurLane };

  return { lane: "spur", side, altLane: null, spurLane };
}

/**
 * その位置で線をどちらへ向いているか。**便ではなく区間の属性。**
 *
 * 位置の番号が**増えるほうへ向いていれば forward、減るなら backward**。
 *
 * ## 便ごとに 1 つに決めてはいけない
 *
 * **ループ便は途中で向きが変わる。**21B は蔵本から香椎浜・照葉を通って
 * 雁の巣まで下り、和白から 3 号線を上って天神へ帰る（28 便）。バケツに
 * 入れた向きをそのまま使うと、折り返したあとも下りの側に貼り付いたままに
 * なる（帯の左右も、矢印の向きも変わらない）。
 *
 * **進むたびに引き直す。**バックエンドが決めるのはバケツに入れる向きだけ。
 *
 * ## ひと停留所ぶんの前後では決めない
 *
 * **模式図は地形を 1 本の軸に畳んでいるので、進んでいるのに番号が戻る
 * 区間がある。**1 番は御幸町（27.0）から千早六丁目（27.693）へ出て、
 * そこから香椎浜車庫（27.387）へ**戻る。**そこだけ逆向きにすると、札が
 * 線の反対側へ跳んで 2 分後にまた戻る。
 *
 * だから**まとまった移動で決める。**DIRECTION_MIN（0.5 節＝40px）に
 * 届くまで先の段を足していき、その符号を採る。折り返し（21B は雁の巣で
 * 7 節戻る）は 1 段で届くので、ループの向きはちゃんと変わる。
 *
 * @returns {string} "forward" か "backward"。決まらなければ fallback
 */
export function dirOf(path, index, fallback = "forward") {
  if (! Array.isArray(path) || path.length < 2) return fallback;

  let sum = 0;
  for (let k = Math.max(0, index); k + 1 < path.length; k++) {
    sum += path[k + 1].node - path[k].node;
    if (Math.abs(sum) >= DIRECTION_MIN) return sum > 0 ? "forward" : "backward";
  }
  // 先で届かなかった（並びの終わりが近い）。**来たほうから決める**
  sum = 0;
  for (let k = Math.min(index, path.length - 1); k > 0; k--) {
    sum += path[k].node - path[k - 1].node;
    if (Math.abs(sum) >= DIRECTION_MIN) return sum > 0 ? "forward" : "backward";
  }

  return fallback;
}

/**
 * 2 つの段のうち、バスを乗せるほう。どちらでもなければ null（帯の脇）。
 *
 *   片方だけ支線     その支線
 *   同じ支線         そのまま
 *   親子の支線       **分かれたほう**（照葉は香椎浜から分かれる）
 *   繋がっていない   null — **どちらの線にも道が無い**（快速 1 番の
 *                    香椎浜北公園前 ⇔ 千早駅）
 */
function outerOf(here, next, spurs) {
  const of = (entry) =>
    entry?.spur ? (spurs || []).find((s) => s.key === entry.spur) : null;
  const a = of(here);
  const b = of(next);

  if (! a) return b ? next : null;
  if (! b || a.key === b.key) return here;
  if (b.host === a.key) return next;   // next が分かれたほう
  if (a.host === b.key) return here;   // here が分かれたほう

  return null;
}

/**
 * いま都市高速を走っているなら、その迂回線と両端のランプ名。
 *
 * ## ランプ名は両端から引く
 *
 * 段に出てくるランプは**支線から乗る側だけ**（名島運動公園前・イオンモール
 * 香椎浜）。降りた次が節になる側は段に出ない（呉服町ランプ → 蔵本、
 * 香椎ランプ → 福岡女子大前、天神北ランプ → 天神中央郵便局前）。設定は
 * ランプが**線のどこにあるか**を持っているので、そこから引く。
 *
 * @returns {{bypass: Object, from: string|null, to: string|null}|null}
 */
export function expressOf(path, index, moving, bypasses) {
  if (! Array.isArray(path) || ! Array.isArray(bypasses) || ! moving) return null;

  const here = path[index];
  const next = path[index + 1];
  if (! here || ! next || ! here.express) return null;

  const bypass = bypasses.find((b) => b.label === here.express);
  if (! bypass) return null;

  return { bypass, from: rampAt(bypass, here), to: rampAt(bypass, next) };
}

/** その段で使うランプ。段が名前を持っていなければ、その位置にあるランプ */
function rampAt(bypass, entry) {
  if (entry.ramp) return entry.ramp;

  return (bypass.ramps || []).find((r) => r.node === entry.node)?.name ?? null;
}
